/*
 * Mission Control — MC1 SHELL, ROUTING & VISUAL CORE
 * ---------------------------------------------------------------
 * Approved artwork lives in /public/mc/*.png. This file NEVER
 * transforms the source artwork — everything visual is either the
 * raw asset or restrained CSS around it.
 *
 * Fonts: Oxanium (display), Space Grotesk (body). Loaded in
 * public/index.html.
 *
 * Scope discipline: MC1 renders the shell + Nexus ecosystem
 * navigation. No Recognition, no Mission Queue, no Governance
 * functionality, no Bot AI, no Supporter tiers. See MC1 spec §2.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { Bell, Menu, X, Copy, Settings as SettingsIcon, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useWalletAuth } from '../contexts/WalletAuthContext';
import MissionControlNavRail from '../components/MissionControlNavRail';

/* Ecosystem — MC1 V3 desktop composition (locked). Wide two-column
 * layout modelled on the original approved ecosystem-map reference.
 * Product-position swap is preserved: Elimination takes the prominent
 * right-of-Nexus top slot that SOL Tracker held in the original concept;
 * SOL Tracker moves to Elimination's former slot. Product node housings
 * carry ONLY approved artwork + product name (spec §6). */
const ECOSYSTEM = [
  // side       row (1=top, 2=mid, 3=low)  name          artwork                              href                            accent
  { id: 'poker',       side: 'left',  row: 1, name: 'Degens Poker',  art: '/mc/product-poker.png',       href: '/mission-control/poker', accent: '#B980FF' },
  { id: 'elimination', side: 'right', row: 1, name: 'Elimination',    art: '/mc/product-elimination.png', href: '/elimination-game',      accent: '#FF3355' },
  { id: 'splitter',    side: 'left',  row: 2, name: 'Wallet Splitter',art: '/mc/product-splitter.png',    href: '/splitter',              accent: '#14F195' },
  { id: 'soltracker',  side: 'right', row: 2, name: 'SOL Tracker',    art: '/mc/product-soltracker.png',  href: '/soltracker/dashboard',  accent: '#5FE7FF' },
  { id: 'airdropper',  side: 'left',  row: 3, name: 'Airdropper',     art: '/mc/product-airdropper.png',  href: '/airdropper',            accent: '#5FE7FF' },
  { id: 'collage',     side: 'right', row: 3, name: 'NFT Collage',    mobileName: 'NFT Collage Creator', art: '/mc/product-collage.png',     href: '/collage',               accent: '#FF7AD1' },
];

// Nav destinations moved to shared MissionControlNavRail component
// so teaser pages render the exact same list. See
// /components/MissionControlNavRail.jsx.

function shortWallet(w) { return !w ? '' : `${w.slice(0, 4)}...${w.slice(-4)}`; }

/* V3 desktop stage geometry — wide, compact, Nexus-centred. All
 * coordinates share one SVG-space so connection lines and housings
 * align pixel-for-pixel. */
const STAGE_W = 1360;
const STAGE_H = 880;
const CX = STAGE_W / 2;
const CY = 420;               // Nexus vertical centre
const NEXUS_R = 130;          // half-size of Nexus artwork
const HOUSING_W = 325;
const HOUSING_H = 106;
// Row Y anchors (housing centre y) — left/right columns share these.
const ROW_Y = [220, 380, 540];
// Left/right housing centre x
const LEFT_X = 232;
const RIGHT_X = STAGE_W - 232;
// Bot at top-centre — repositioned DOWN closer to Nexus (v-polish).
const BOT_CX = CX;
const BOT_CY = 200;
const BOT_W = 440;
const BOT_H = 148;
// DEGENS DAO wordmark — moved ABOVE the Bot tile (v-polish).
const WORDMARK_TOP = 24;
// P2P at bottom-centre
const P2P_CX = CX;
const P2P_CY = 654;
// Governance below P2P
const GOV_CX = CX;
const GOV_CY = 780;
const GOV_W = 358;
const GOV_H = 112;

const AgentPanel = ({ open, onClose, wallet, username }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-y-0 right-0 z-40 w-full max-w-md bg-[#080A0F] border-l border-white/8 shadow-2xl overflow-y-auto"
         data-testid="agent-panel">
      <div className="p-5 border-b border-white/8 flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40" style={{fontFamily:'Space Grotesk'}}>Agent Panel</p>
          <h3 className="text-white text-lg" style={{fontFamily:'Oxanium', fontWeight:600}}>Degens Bot</h3>
          <p className="text-white/50 text-xs mt-0.5" style={{fontFamily:'Space Grotesk'}}>Agent of the Degens Ecosystem</p>
        </div>
        <button onClick={onClose} data-testid="agent-panel-close" className="p-2 rounded-lg hover:bg-white/5 text-white/60 hover:text-white transition"><X className="w-4 h-4"/></button>
      </div>
      <div className="p-5">
        <div className="flex items-start gap-4">
          <img src="/mc/agent-bot.png" alt="" className="w-24 h-24 object-contain flex-shrink-0 drop-shadow-[0_0_20px_rgba(20,241,149,0.35)]" />
          <div className="text-white/70 text-sm leading-relaxed" style={{fontFamily:'Space Grotesk'}}>
            {username
              ? <>Standing by, <span className="text-white font-medium">{username}</span>. When Recognition ships in MC2 this panel will speak up when something in the ecosystem needs your attention.</>
              : <>Standing by. Connect a wallet and the Agent will hold your ecosystem context here.</>
            }
          </div>
        </div>
        {wallet && (
          <div className="mt-6 p-4 rounded-xl border border-white/8 bg-white/[0.02]">
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2" style={{fontFamily:'Space Grotesk'}}>Current Wallet</p>
            <p className="text-white font-mono text-xs break-all">{wallet}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const NotificationDrawer = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-y-0 right-0 z-40 w-full max-w-sm bg-[#080A0F] border-l border-white/8 shadow-2xl overflow-y-auto"
         data-testid="notifications-drawer">
      <div className="p-5 border-b border-white/8 flex items-center justify-between">
        <h3 className="text-white text-lg" style={{fontFamily:'Oxanium', fontWeight:600}}>Notifications</h3>
        <button onClick={onClose} data-testid="notifications-close" className="p-2 rounded-lg hover:bg-white/5 text-white/60 hover:text-white transition"><X className="w-4 h-4"/></button>
      </div>
      {/* MC1 spec §14 — no fake counts, no invented notifications. */}
      <div className="p-6 text-center text-white/40 text-sm" style={{fontFamily:'Space Grotesk'}}>
        Notifications will appear here as the ecosystem has something real to tell you.
      </div>
    </div>
  );
};

const ProfilePopover = ({ open, onClose, wallet, username, onDisconnect }) => {
  const navigate = useNavigate();
  if (!open) return null;
  const copy = () => { if (wallet) { navigator.clipboard.writeText(wallet); toast.success('Wallet address copied'); } };
  return (
    <div className="absolute right-0 top-full mt-2 w-72 rounded-xl border border-white/10 bg-[#0B0E14] shadow-2xl z-50 overflow-hidden" data-testid="profile-popover">
      <div className="p-4 border-b border-white/8">
        {username ? (
          <>
            <p className="text-white text-sm" style={{fontFamily:'Oxanium', fontWeight:600}}>{username}</p>
            <p className="text-white/50 text-xs font-mono mt-1" data-testid="profile-short-wallet">{shortWallet(wallet)}</p>
          </>
        ) : (
          <p className="text-white font-mono text-sm" data-testid="profile-short-wallet">{shortWallet(wallet)}</p>
        )}
      </div>
      <div className="p-3 border-b border-white/8">
        <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1" style={{fontFamily:'Space Grotesk'}}>Full Address</p>
        <div className="flex items-center gap-2">
          <p className="text-white/70 text-[11px] font-mono break-all flex-1">{wallet}</p>
          <button onClick={copy} data-testid="profile-copy-address" className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition"><Copy className="w-3.5 h-3.5"/></button>
        </div>
      </div>
      <button onClick={() => { onClose(); navigate('/settings'); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-white/70 hover:bg-white/5 hover:text-white transition text-sm text-left" style={{fontFamily:'Space Grotesk'}} data-testid="profile-settings-link">
        <SettingsIcon className="w-4 h-4"/> Settings
      </button>
      <button onClick={() => { onClose(); onDisconnect(); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-red-400 hover:bg-red-500/10 transition text-sm text-left" style={{fontFamily:'Space Grotesk'}} data-testid="profile-disconnect">
        <LogOut className="w-4 h-4"/> Disconnect
      </button>
    </div>
  );
};

const MissionControl = () => {
  const { connected, publicKey } = useWallet();
  const { setVisible } = useWalletModal();
  const { isAuthenticated, isVerifying, signOut } = useWalletAuth();
  const navigate = useNavigate();
  const walletAddr = publicKey?.toString();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [username, setUsername] = useState(null);
  const [entered, setEntered] = useState(false);
  const [hoverNode, setHoverNode] = useState(null); // id of hovered product for connection glow

  /* MC1 spec §12 — restrained first-session reveal, respects
   * prefers-reduced-motion. */
  useEffect(() => {
    const rm = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (rm) { setEntered(true); return; }
    const t = setTimeout(() => setEntered(true), 30);
    return () => clearTimeout(t);
  }, []);

  /* Fetch existing username from the profile endpoint. Only pull the
   * username once SIWS authentication has actually succeeded — before
   * that, publicKey exists but the server won't recognise the session
   * for this wallet. */
  useEffect(() => {
    if (!isAuthenticated || !walletAddr) { setUsername(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        const r = await fetch(`${API}/api/me`, { credentials: 'include' });
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setUsername(j && j.username ? j.username : null);
      } catch { /* silent — MC1 shows short wallet as fallback */ }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, walletAddr]);

  const handleConnect = () => setVisible(true);
  /*
   * Explicit disconnect must invalidate the Degens SIWS session, not
   * just the wallet adapter connection. `signOut` (WalletAuthContext)
   * calls POST /api/auth/logout to delete the server-side session +
   * clear the HttpOnly cookie, then clears frontend authenticated state,
   * then disconnects the wallet adapter. Without this, reconnecting the
   * same wallet inherits the previous session and skips SIWS.
   */
  const handleDisconnect = useCallback(async () => {
    try { localStorage.removeItem('wallet_address'); } catch { /* noop */ }
    await signOut();
    navigate('/');
  }, [signOut, navigate]);

  const handleNodeClick = (node) => {
    if (node.id === 'agent') { setAgentOpen(true); return; }
    if (node.href.startsWith('#')) return;
    navigate(node.href);
  };

  const identityLabel = useMemo(() => {
    // Only surface the final identity once SIWS has actually verified.
    if (!isAuthenticated) return 'Verifying…';
    return username || shortWallet(walletAddr);
  }, [isAuthenticated, username, walletAddr]);

  return (
    <div className="min-h-screen bg-[#080B12] text-white overflow-x-hidden" style={{fontFamily:'Space Grotesk'}} data-testid="mission-control-page">
      {/* Ambient depth — restrained, spec §8 (lifted for legibility). */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(90,180,255,0.07),transparent_65%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(185,128,255,0.06),transparent_65%)]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[75vmin] h-[75vmin] rounded-full bg-[radial-gradient(closest-side,rgba(90,180,255,0.08),transparent_70%)]" />
      </div>

      {/* Top application bar */}
      <header className="relative z-30 h-16 border-b border-white/8 bg-[#080B12]/85 backdrop-blur-md flex items-center px-4 md:px-6 gap-3">
        <button onClick={() => setNavOpen(v=>!v)} data-testid="app-nav-toggle" aria-label="Toggle navigation" className="p-2 rounded-lg hover:bg-white/5 text-white/70 hover:text-white transition">
          <Menu className="w-5 h-5"/>
        </button>
        <Link to="/mission-control" className="flex items-center gap-3">
          <img src="/mc/nexus.png" alt="Degens DAO Nexus" className="w-8 h-8 object-contain" />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40" style={{fontFamily:'Space Grotesk'}}>Degens DAO</span>
            <span className="text-white text-sm" style={{fontFamily:'Oxanium', fontWeight:700, letterSpacing:'0.05em'}}>Mission Control</span>
          </div>
        </Link>
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-[#14F195]/25 bg-[#14F195]/5 text-[9px] uppercase tracking-[0.2em] text-[#14F195]" style={{fontFamily:'Space Grotesk'}}>
          <span className="w-1 h-1 rounded-full bg-[#14F195] animate-pulse" /> Controlled Beta
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => setNotifOpen(true)} data-testid="notifications-open" aria-label="Notifications" className="p-2 rounded-lg hover:bg-white/5 text-white/70 hover:text-white transition">
            <Bell className="w-5 h-5"/>
          </button>
          <div className="relative">
            {!connected ? (
              <button onClick={handleConnect} data-testid="mc-connect-wallet" className="px-4 py-2 rounded-lg bg-[#14F195] text-black text-sm hover:bg-[#14F195]/90 transition" style={{fontFamily:'Oxanium', fontWeight:600, letterSpacing:'0.03em'}}>
                Connect Wallet
              </button>
            ) : !isAuthenticated ? (
              /* Wallet adapter is connected but the SIWS handshake has not
                 completed yet — do NOT show the final identity. Present a
                 restrained transitional state instead. Not clickable. */
              <div data-testid="mc-verifying" className="px-3 py-2 rounded-lg border border-[#14F195]/30 bg-[#14F195]/[0.06] text-white/80 text-sm flex items-center gap-2" style={{fontFamily:'Space Grotesk', fontWeight:500}}>
                <span className="w-2 h-2 rounded-full bg-[#14F195] animate-pulse" />
                <span>{isVerifying ? 'Verifying…' : 'Connecting…'}</span>
              </div>
            ) : (
              <button onClick={() => setProfileOpen(v=>!v)} data-testid="mc-profile-toggle" className="px-3 py-2 rounded-lg border border-white/10 hover:border-white/20 bg-white/[0.02] hover:bg-white/[0.05] text-white text-sm transition flex items-center gap-2" style={{fontFamily:'Space Grotesk', fontWeight:500}}>
                <span className="w-2 h-2 rounded-full bg-[#14F195]" />
                <span data-testid="mc-identity-label">{identityLabel}</span>
              </button>
            )}
            <ProfilePopover open={profileOpen} onClose={() => setProfileOpen(false)} wallet={walletAddr} username={username} onDisconnect={handleDisconnect} />
          </div>
        </div>
      </header>

      {/* Layout: minimal collapsed left rail + main */}
      <div className="relative z-20 flex">
        <MissionControlNavRail variant="inline" open={navOpen} />

        <main className="flex-1 min-h-[calc(100vh-4rem)] pb-24">
          {/* Ecosystem stage — desktop (V3, per approved reference). */}
          <section className={`hidden md:block relative transition-opacity duration-500 ${entered ? 'opacity-100' : 'opacity-0'}`} aria-label="Degens Ecosystem" data-testid="mc-ecosystem-desktop">
            <div className="mx-auto relative" style={{ width: STAGE_W, maxWidth: '100%', height: STAGE_H }}>
              {(() => {
                // Housing attach points and Nexus attach points.
                const nexusL = { x: CX - NEXUS_R + 4, y: CY };
                const nexusR = { x: CX + NEXUS_R - 4, y: CY };
                const nexusT = { x: CX, y: CY - NEXUS_R + 4 };
                const nexusB = { x: CX, y: CY + NEXUS_R - 4 };
                // Product housing attach points (inner edge of each housing).
                const housingAttach = (side, rowIdx) => {
                  const y = ROW_Y[rowIdx];
                  const x = side === 'left' ? LEFT_X + HOUSING_W / 2 : RIGHT_X - HOUSING_W / 2;
                  // Attach ~24px from the housing centre toward the nexus so the
                  // connection dot sits on the housing edge, not overlapping the label.
                  return { x, y };
                };
                return (
                  <svg viewBox={`0 0 ${STAGE_W} ${STAGE_H}`} className="absolute inset-0 w-full h-full pointer-events-none" data-testid="mc-connection-network">
                    <defs>
                      <radialGradient id="mcNexusHalo" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(90,180,255,0.22)" />
                        <stop offset="55%" stopColor="rgba(90,180,255,0.06)" />
                        <stop offset="100%" stopColor="rgba(90,180,255,0)" />
                      </radialGradient>
                    </defs>
                    <circle cx={CX} cy={CY} r={NEXUS_R + 90} fill="url(#mcNexusHalo)" />

                    {/* Product connection lines */}
                    {ECOSYSTEM.map((n) => {
                      const from = housingAttach(n.side, n.row - 1);
                      const to = n.side === 'left' ? nexusL : nexusR;
                      const active = hoverNode === n.id;
                      const stroke = active ? n.accent : 'rgba(200,215,235,0.28)';
                      const width = active ? 1.8 : 1.2;
                      return (
                        <g key={`link-${n.id}`}>
                          <line x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                                stroke={stroke} strokeWidth={width}
                                style={{ transition: 'stroke 200ms ease, stroke-width 200ms ease' }} />
                          {/* Endpoint dots — small glow markers on both ends. */}
                          <circle cx={from.x} cy={from.y} r="3.2" fill={active ? n.accent : 'rgba(210,220,240,0.75)'} />
                          <circle cx={to.x}   cy={to.y}   r="2.4" fill={active ? n.accent : 'rgba(210,220,240,0.55)'} />
                        </g>
                      );
                    })}

                    {/* Bot ↔ Nexus (top) — Agent connection. Clean, direct, green. */}
                    <line x1={BOT_CX} y1={BOT_CY + BOT_H / 2} x2={nexusT.x} y2={nexusT.y}
                          stroke="rgba(20,241,149,0.55)" strokeWidth="1.6" />
                    <circle cx={BOT_CX} cy={BOT_CY + BOT_H / 2} r="3.4" fill="rgba(20,241,149,0.9)" />
                    <circle cx={nexusT.x} cy={nexusT.y} r="2.8" fill="rgba(20,241,149,0.7)" />

                    {/* Nexus ↔ P2P (bottom-centre chain) */}
                    <line x1={nexusB.x} y1={nexusB.y} x2={P2P_CX} y2={P2P_CY - HOUSING_H / 2}
                          stroke="rgba(255,177,59,0.55)" strokeWidth="1.4" />
                    <circle cx={nexusB.x} cy={nexusB.y} r="2.8" fill="rgba(255,177,59,0.7)" />
                    <circle cx={P2P_CX} cy={P2P_CY - HOUSING_H / 2} r="3.2" fill="rgba(255,177,59,0.9)" />

                    {/* P2P ↔ Governance (extension chain, dashed purple). */}
                    <line x1={P2P_CX} y1={P2P_CY + HOUSING_H / 2} x2={GOV_CX} y2={GOV_CY - GOV_H / 2}
                          stroke="rgba(185,128,255,0.55)" strokeWidth="1.4" strokeDasharray="4 4" />
                    <circle cx={P2P_CX} cy={P2P_CY + HOUSING_H / 2} r="2.8" fill="rgba(185,128,255,0.75)" />
                    <circle cx={GOV_CX} cy={GOV_CY - GOV_H / 2} r="3.2" fill="rgba(185,128,255,0.95)" />
                  </svg>
                );
              })()}

              {/* Central Nexus (locked V2 treatment). DEGENS DAO
                  wordmark moved ABOVE the Bot tile — see below. */}
              <div className="absolute" style={{ left: CX - NEXUS_R, top: CY - NEXUS_R, width: NEXUS_R * 2, height: NEXUS_R * 2 }}>
                <img src="/mc/nexus.png" alt="Degens DAO Nexus"
                     className="mc-nexus-breathe w-full h-full object-contain relative z-[2]"
                     data-testid="mc-nexus" draggable={false} />
              </div>
              {/* DEGENS DAO / MISSION CONTROL brand lockup — TOP of
                  the ecosystem stage, ABOVE the Degens Bot tile.
                  Horizontal single-line heading + smaller cyan
                  subtitle (v-polish correction). Uses only Oxanium
                  (already loaded); no new fonts. */}
              <div className="absolute text-center pointer-events-none select-none"
                   style={{ left: CX - 260, top: WORDMARK_TOP, width: 520 }}
                   data-testid="mc-dao-wordmark">
                <p className="leading-none"
                   style={{ fontFamily: 'Oxanium', fontWeight: 800, letterSpacing: '0.28em', fontSize: 36 }}>
                  <span className="text-white">DEGENS </span>
                  <span className="text-white">DAO</span>
                </p>
                <div className="mt-2 flex items-center justify-center gap-3"
                     data-testid="mc-mission-control-subtitle">
                  <span aria-hidden="true" className="inline-block h-px w-8 bg-[#5FE7FF]/60" />
                  <span className="text-[#5FE7FF] leading-none"
                        style={{ fontFamily: 'Oxanium', fontWeight: 600, letterSpacing: '0.42em', fontSize: 12 }}>
                    MISSION CONTROL
                  </span>
                  <span aria-hidden="true" className="inline-block h-px w-8 bg-[#5FE7FF]/60" />
                </div>
              </div>

              {/* Degens Bot — top-centre Agent housing (locked wording, no AI). */}
              <button onClick={() => setAgentOpen(true)}
                      data-testid="mc-node-agent"
                      aria-label="Degens Bot — Agent of the Degens Ecosystem"
                      className="mc-housing absolute group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14F195]/50 rounded-3xl"
                      style={{ left: BOT_CX - BOT_W / 2, top: BOT_CY - BOT_H / 2, width: BOT_W, height: BOT_H, '--accent': '#14F195' }}>
                <div className="w-full h-full flex items-center gap-4 px-5">
                  <img src="/mc/agent-bot.png" alt="Degens Bot"
                       className="w-32 h-32 object-contain flex-shrink-0 -my-3 drop-shadow-[0_0_22px_rgba(20,241,149,0.5)]"
                       draggable={false} />
                  <div className="text-left leading-tight">
                    <p className="text-white text-[18px]" style={{ fontFamily: 'Oxanium', fontWeight: 800, letterSpacing: '0.06em' }}>DEGENS BOT</p>
                    <p className="text-white/70 text-[12px] mt-0.5" style={{ fontFamily: 'Space Grotesk' }}>Agent of the</p>
                    <p className="text-[#14F195] text-[12px]" style={{ fontFamily: 'Space Grotesk', fontWeight: 600 }}>Degens Ecosystem</p>
                  </div>
                </div>
              </button>

              {/* Left column and Right column product node housings. */}
              {ECOSYSTEM.map((n) => {
                const cy = ROW_Y[n.row - 1];
                const cx = n.side === 'left' ? LEFT_X : RIGHT_X;
                return (
                  <button key={n.id}
                          onMouseEnter={() => setHoverNode(n.id)}
                          onMouseLeave={() => setHoverNode(prev => (prev === n.id ? null : prev))}
                          onFocus={() => setHoverNode(n.id)}
                          onBlur={() => setHoverNode(prev => (prev === n.id ? null : prev))}
                          onClick={() => handleNodeClick(n)}
                          data-testid={`mc-node-${n.id}`}
                          aria-label={n.name}
                          className="mc-housing absolute group focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 rounded-3xl"
                          style={{ left: cx - HOUSING_W / 2, top: cy - HOUSING_H / 2, width: HOUSING_W, height: HOUSING_H, '--accent': n.accent }}>
                    <div className={`w-full h-full flex items-center gap-4 px-4 ${n.side === 'right' ? 'flex-row-reverse text-right' : ''}`}>
                      <div className="w-24 h-24 flex items-center justify-center flex-shrink-0 rounded-2xl bg-white/[0.03]">
                        <img src={n.art} alt={n.name} className="w-full h-full object-contain drop-shadow-[0_0_16px_rgba(255,255,255,0.12)]" draggable={false} />
                      </div>
                      <div className={`flex-1 ${n.side === 'right' ? 'text-right' : 'text-left'}`}>
                        <p className="text-white text-[16px]" style={{ fontFamily: 'Oxanium', fontWeight: 800, letterSpacing: '0.08em' }}>{n.name.toUpperCase()}</p>
                      </div>
                    </div>
                  </button>
                );
              })}

              {/* P2P Trade — bottom-centre housing. */}
              <button onClick={() => handleNodeClick({ id: 'p2p', href: '/p2p-trade' })}
                      onMouseEnter={() => setHoverNode('p2p')}
                      onMouseLeave={() => setHoverNode(prev => (prev === 'p2p' ? null : prev))}
                      data-testid="mc-node-p2p"
                      aria-label="P2P Trade"
                      className="mc-housing absolute group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFB13B]/50 rounded-3xl"
                      style={{ left: P2P_CX - HOUSING_W / 2, top: P2P_CY - HOUSING_H / 2, width: HOUSING_W, height: HOUSING_H, '--accent': '#FFB13B' }}>
                <div className="w-full h-full flex items-center gap-4 px-4">
                  <div className="w-24 h-24 flex items-center justify-center flex-shrink-0 rounded-2xl bg-white/[0.03]">
                    <img src="/mc/product-p2p.png" alt="P2P Trade" className="w-full h-full object-contain" draggable={false} />
                  </div>
                  <div className="text-left">
                    <p className="text-white text-[16px]" style={{ fontFamily: 'Oxanium', fontWeight: 800, letterSpacing: '0.08em' }}>P2P TRADE</p>
                  </div>
                </div>
              </button>

              {/* Governance — DAO Oversight, directly below the Nexus chain. */}
              <button onClick={() => navigate('/mission-control/governance')}
                      data-testid="mc-node-governance"
                      aria-label="Governance — DAO Oversight"
                      className="mc-housing absolute group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B980FF]/50 rounded-3xl"
                      style={{ left: GOV_CX - GOV_W / 2, top: GOV_CY - GOV_H / 2, width: GOV_W, height: GOV_H, '--accent': '#B980FF' }}>
                <div className="w-full h-full flex items-center gap-4 px-5">
                  <img src="/mc/product-governance.png" alt="Governance"
                       className="w-24 h-24 object-contain flex-shrink-0 drop-shadow-[0_0_18px_rgba(185,128,255,0.4)]"
                       draggable={false} />
                  <div className="text-left leading-tight">
                    <p className="text-white text-[16px]" style={{ fontFamily: 'Oxanium', fontWeight: 800, letterSpacing: '0.08em' }}>GOVERNANCE</p>
                    <p className="text-[#CDB4FF] text-[11px] uppercase tracking-[0.22em] mt-1" style={{ fontFamily: 'Space Grotesk' }}>DAO Oversight</p>
                  </div>
                </div>
              </button>
            </div>
          </section>

          {/* Mobile ecosystem — updated to match V3 language (no invented microcopy, no FLAGSHIP). */}
          <section className={`md:hidden px-4 pt-6 pb-4 space-y-5 transition-opacity duration-500 ${entered ? 'opacity-100' : 'opacity-0'}`} data-testid="mc-ecosystem-mobile">
            <div className="flex flex-col items-center">
              <img src="/mc/nexus.png" alt="Degens DAO Nexus" className="mc-nexus-breathe w-40 h-40 object-contain" data-testid="mc-nexus-mobile"/>
              <p className="mt-2 text-white text-lg leading-none" style={{fontFamily:'Oxanium', fontWeight:800, letterSpacing:'0.08em'}}>DEGENS <span className="text-[#14F195]">DAO</span></p>
            </div>
            <button onClick={() => setAgentOpen(true)} className="mc-housing w-full flex items-center gap-3 rounded-2xl p-3" style={{'--accent':'#14F195'}} data-testid="mc-mobile-agent">
              <img src="/mc/agent-bot.png" alt="" className="w-14 h-14 object-contain"/>
              <div className="text-left">
                <p className="text-white text-sm" style={{fontFamily:'Oxanium', fontWeight:800, letterSpacing:'0.06em'}}>DEGENS BOT</p>
                <p className="text-[#14F195] text-[10px] uppercase tracking-[0.18em]" style={{fontFamily:'Space Grotesk'}}>Agent of the Degens Ecosystem</p>
              </div>
            </button>
            <div className="grid grid-cols-2 gap-3">
              {ECOSYSTEM.map((n) => (
                <button key={n.id} onClick={() => handleNodeClick(n)} data-testid={`mc-mobile-node-${n.id}`} className="mc-housing rounded-2xl p-3 flex flex-col items-center gap-2 min-h-[7.5rem]" style={{'--accent':n.accent}}>
                  <img src={n.art} alt={n.name} className="w-14 h-14 object-contain"/>
                  <p className="text-white text-xs text-center leading-tight" style={{fontFamily:'Oxanium', fontWeight:800, letterSpacing:'0.06em'}}>{(n.mobileName || n.name).toUpperCase()}</p>
                </button>
              ))}
              <button onClick={() => handleNodeClick({ id: 'p2p', href: '/p2p-trade' })} data-testid="mc-mobile-node-p2p" className="mc-housing rounded-2xl p-3 flex flex-col items-center gap-2 min-h-[7.5rem] col-span-2" style={{'--accent':'#FFB13B'}}>
                <img src="/mc/product-p2p.png" alt="P2P Trade" className="w-14 h-14 object-contain"/>
                <p className="text-white text-xs text-center" style={{fontFamily:'Oxanium', fontWeight:800, letterSpacing:'0.06em'}}>P2P TRADE</p>
              </button>
            </div>
            <button onClick={() => navigate('/mission-control/governance')} className="mc-housing w-full flex items-center gap-3 rounded-2xl p-3" style={{'--accent':'#B980FF'}} data-testid="mc-mobile-governance">
              <img src="/mc/product-governance.png" alt="" className="w-14 h-14 object-contain"/>
              <div className="text-left">
                <p className="text-white text-sm" style={{fontFamily:'Oxanium', fontWeight:800, letterSpacing:'0.06em'}}>GOVERNANCE</p>
                <p className="text-[#CDB4FF] text-[10px] uppercase tracking-[0.18em]" style={{fontFamily:'Space Grotesk'}}>DAO Oversight</p>
              </div>
            </button>
          </section>

          {/* "Your Mission Control" foundation — spec §18. Empty scaffold. */}
          <section className="max-w-6xl mx-auto px-6 pt-4 pb-16" data-testid="mc-your-mission-control">
            <div className="mb-3 flex items-baseline gap-3">
              <h2 className="text-white text-xl" style={{fontFamily:'Oxanium', fontWeight:700, letterSpacing:'0.05em'}}>Your Mission Control</h2>
              <span className="text-white/40 text-[10px] uppercase tracking-[0.22em]" style={{fontFamily:'Space Grotesk'}}>Contextual</span>
            </div>
            <div className="text-white/50 text-sm" style={{fontFamily:'Space Grotesk'}}>
              {connected
                ? 'Modules will appear here when the ecosystem has something for you.'
                : 'Connect a wallet to personalise this space.'}
            </div>
          </section>
        </main>
      </div>

      <AgentPanel open={agentOpen} onClose={() => setAgentOpen(false)} wallet={walletAddr} username={username}/>
      <NotificationDrawer open={notifOpen} onClose={() => setNotifOpen(false)}/>

      {/* MC1 spec §10 — restrained breathing + housing accent styling. */}
      <style>{`
        @keyframes mcNexusBreath {
          0%   { transform: scale(1);    filter: drop-shadow(0 0 24px rgba(20,241,149,0.18)); }
          50%  { transform: scale(1.03); filter: drop-shadow(0 0 32px rgba(20,241,149,0.28)); }
          100% { transform: scale(1);    filter: drop-shadow(0 0 24px rgba(20,241,149,0.18)); }
        }
        .mc-nexus-breathe { animation: mcNexusBreath 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .mc-nexus-breathe { animation: none; }
        }
        /* Product node housings — restrained dark/graphite container with a
         * subtle accent border drawn from the product artwork. Never overpowers
         * the artwork; hover slightly lifts the accent glow only. */
        .mc-housing {
          background: linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.015));
          border: 1px solid color-mix(in oklab, var(--accent) 32%, rgba(255,255,255,0.08));
          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,0.03),
            0 6px 18px rgba(0,0,0,0.35),
            0 0 24px -8px color-mix(in oklab, var(--accent) 45%, transparent);
          transition: box-shadow 220ms ease, transform 220ms ease, border-color 220ms ease;
        }
        .mc-housing:hover, .mc-housing:focus-visible {
          border-color: color-mix(in oklab, var(--accent) 58%, rgba(255,255,255,0.10));
          box-shadow:
            inset 0 0 0 1px rgba(255,255,255,0.04),
            0 8px 24px rgba(0,0,0,0.45),
            0 0 34px -6px color-mix(in oklab, var(--accent) 65%, transparent);
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
};

export default MissionControl;
